import DataTable from './DataTable.jsx';
import { useAutoSourceSync } from '../store/useAutoSourceSync.js';
import { SectionShell } from './FormSection.jsx';

const RE_READONLY_COLS = ['description', 'type', 'payment', 'amount', 'interestRate', 'term'];

// Type options align with eMoney liability resources. Salesforce routes:
//   Auto/Personal/Other -> FixedTermLiability, Credit Card / HELOC ->
//   CreditLineLiability, Student Loan -> StudentLoans. Blank defaults to
//   FixedTermLiability downstream. Mortgage is the read-only default for rows
//   auto-created from a Real Estate asset (home loans live in Assets); it's
//   listed here so those rows render "Mortgage" instead of a blank cell.
//   NOTE: if the Salesforce liability Type is a picklist, it must include
//   "Mortgage" or the value will be rejected on save.
const LIABILITY_TYPE_OPTIONS = [
  'Mortgage',
  'Auto Loan',
  'Personal Loan',
  'Credit Card',
  'Line of Credit / HELOC',
  'Student Loan',
  'Other',
];

const LIABILITIES_TABLE = {
  key: 'items',
  title: '',
  ariaTitle: 'Liabilities',
  columns: [
    { key: '_reKey', label: '', hidden: true },
    { key: 'description', label: 'Description', colClass: 'col-wide' },
    { key: 'type', label: 'Type', type: 'select', options: LIABILITY_TYPE_OPTIONS },
    { key: 'payment', label: 'Payment (P&I)', type: 'currency', labelInfo: 'Monthly' },
    { key: 'amount', label: 'Amount', type: 'currency', labelInfo: 'Outstanding balance — this is what drives Total Liabilities (a payment with no balance is counted as an expense, not a liability).' },
    { key: 'interestRate', label: 'Int. Rate', type: 'percent' },
    { key: 'term', label: 'Term', colClass: 'col-narrow', labelInfo: 'In years', numeric: true },
  ],
  starterRows: [],
};

export default function LiabilitiesSection({ section, num }) {
  useAutoSourceSync({
    targetPath: 'liabilities.items',
    matchKeyField: '_reKey',
    // After a Salesforce load the _reKey is gone; reclaim the loaded mortgage
    // row by its description so it isn't duplicated.
    naturalKey: r => {
      const d = (r?.description || '').trim().toLowerCase();
      return d.endsWith('mortgage') ? 'liab:' + d : '';
    },
    computeAutoRows: data => {
      const re = Array.isArray(data?.assets?.realEstate) ? data.assets.realEstate : [];
      return re
        .filter(r => r?.remainingLoan && parseFloat(r.remainingLoan) > 0)
        .map((r, i) => {
          // EVERY property with a loan gets its mortgage row. Requiring
          // _autoKey skipped properties added via "+ Add" — only the three
          // checkbox rows carry it, so their loans never flowed to Liabilities
          // (and therefore never to Expenses). Manual rows key off their own
          // uid; checkbox rows keep their re:ownHome-style key so saved rows
          // still reclaim on reload.
          const key = r._autoKey || r._uid || `row${i}`;
          return {
            _reKey: key,
            description: (r.description || 'RE Loan') + ' Mortgage',
            type: 'Mortgage',
            payment: r.payment || '',
            amount: r.remainingLoan,
            interestRate: r.interestRate || '',
            term: r.term || '',
            _readOnly: RE_READONLY_COLS,
          };
        });
    },
  });

  return (
    <SectionShell section={section} num={num}>
      <p className="section-note">
        Known loan liabilities from the Assets section are prefilled below. Add any additional liabilities.
      </p>
      <DataTable sectionId="liabilities" tableDef={LIABILITIES_TABLE} />
    </SectionShell>
  );
}
